import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { promptsApi } from '../services/api';
import { SectionCard, useToast } from '@vmcom/ui';

interface PromptData {
  name: string;
  content: string;
  description: string;
}

const monoField = { fontFamily: 'monospace', fontSize: '13px' };

const PromptsSettings: React.FC = () => {
  const { toast } = useToast();
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('psystem');

  const promptConfigs: Record<string, PromptData> = useMemo(
    () => ({
      psystem: {
        name: 'psystem',
        content: '',
        description:
          'Системный промпт задает контекст общения и правила для всех брендов. Этот промпт используется один раз перед генерацией для всех брендов.',
      },
      pbrand: {
        name: 'pbrand',
        content: '',
        description:
          'Промпт для бренда формирует конкретный запрос на текст с учетом системного промпта. Используется для каждого бренда отдельно.',
      },
      town: {
        name: 'town',
        content: '',
        description: 'Промпт для города содержит информацию о городе продавца. Используется как переменная в других промптах.',
      },
      psytemfag: {
        name: 'psytemfag',
        content: '',
        description: 'Системный промпт для FAQ задает тон и правила для генерации страницы вопросов-ответов для брендов.',
      },
      ptasckfaq: {
        name: 'ptasckfaq',
        content: '',
        description: 'Промпт задачи для FAQ определяет что нужно сформировать на странице вопросов-ответов для каждого бренда.',
      },
      psytemcategory: {
        name: 'psytemcategory',
        content: '',
        description: 'Системный промпт для категорий задает тон и правила для генерации SEO текста для категорий товаров.',
      },
      ptasckcategory: {
        name: 'ptasckcategory',
        content: '',
        description: 'Промпт задачи для категорий определяет что нужно сформировать в SEO тексте для каждой категории товаров.',
      },
      psytemvetka: {
        name: 'psytemvetka',
        content: '',
        description: 'Системный промпт для веток ассортимента задает тон и правила для генерации SEO текста веток.',
      },
      ptasckvetka: {
        name: 'ptasckvetka',
        content: '',
        description: 'Промпт задачи для веток ассортимента определяет что нужно сформировать в SEO тексте для каждой ветки.',
      },
      psystemproductfaq: {
        name: 'psystemproductfaq',
        content: '',
        description: 'Системный промпт для каркаса ЧАВО товаров (5 вопросов → ветка ассортимента).',
      },
      ptaskproductfaq: {
        name: 'ptaskproductfaq',
        content: '',
        description: 'Задача генерации структурированного dialog (steps A–E, outcome, scenarios) для категории/ветки.',
      },
    }),
    [],
  );

  const loadPrompts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const formData: Record<string, string> = {};
      try {
        formData.seo_requirements = await promptsApi.getPromptContent('seo_requirements');
      } catch (err) {
        console.error('Error loading seo_requirements:', err);
        formData.seo_requirements = '';
      }
      for (const [key, config] of Object.entries(promptConfigs)) {
        try {
          formData[key] = await promptsApi.getPromptContent(config.name);
        } catch (err) {
          console.error(`Error loading ${config.name}:`, err);
          formData[key] = '';
        }
      }
      setValues(formData);
    } catch (err) {
      setError('Ошибка загрузки промптов');
      console.error('Error loading prompts:', err);
    } finally {
      setLoading(false);
    }
  }, [promptConfigs]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const setField = (key: string, content: string) => {
    setValues((prev) => ({ ...prev, [key]: content }));
  };

  const handleSave = async () => {
    for (const [key, config] of Object.entries(promptConfigs)) {
      const content = values[key] ?? '';
      if (!content.trim()) {
        toast(`Промпт ${config.name} не может быть пустым`, 'warning');
        return;
      }
      if (content.length < 10) {
        toast(`Промпт ${config.name} должен содержать минимум 10 символов`, 'warning');
        return;
      }
    }
    try {
      setSaving(true);
      setError(null);
      if (typeof values.seo_requirements === 'string') {
        await promptsApi.updatePrompt('seo_requirements', values.seo_requirements);
      }
      for (const [key, content] of Object.entries(values)) {
        if (promptConfigs[key] && typeof content === 'string') {
          await promptsApi.updatePrompt(promptConfigs[key].name, content);
        }
      }
      toast('Промпты успешно сохранены!', 'success');
    } catch (err) {
      setError('Ошибка сохранения промптов');
      console.error('Error saving prompts:', err);
    } finally {
      setSaving(false);
    }
  };

  const activeConfig = promptConfigs[activeTab];

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<RefreshOutlinedIcon />} onClick={loadPrompts} disabled={loading}>
          Обновить
        </Button>
        <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={handleSave} disabled={saving}>
          Сохранить
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !Object.keys(values).length ? (
        <CircularProgress size={28} />
      ) : (
        <>
          <SectionCard title="SEO требования к составлению текстов">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Эти правила добавляются ко всем промптам при генерации текстов. Используйте для единых SEO-норм: тошнота,
              водность, уникальность, ключевые слова и т.д.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={6}
              value={values.seo_requirements ?? ''}
              onChange={(e) => setField('seo_requirements', e.target.value)}
              placeholder="Например: Академическая тошнота до 15%. 10–12 ключевых фраз. Уникальность 85–90%."
              InputProps={{ sx: monoField }}
            />
          </SectionCard>

          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
            {Object.entries(promptConfigs).map(([key, config]) => (
              <Tab key={key} label={config.name} value={key} />
            ))}
          </Tabs>

          {activeConfig && (
            <Box sx={{ px: { xs: 0, sm: 1 } }}>
              <Typography variant="h6" gutterBottom>
                {activeConfig.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <InfoOutlinedIcon fontSize="small" sx={{ mt: 0.25 }} />
                {activeConfig.description}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={20}
                value={values[activeTab] ?? ''}
                onChange={(e) => setField(activeTab, e.target.value)}
                placeholder={`Введите содержимое промпта ${activeConfig.name}...`}
                InputProps={{ sx: monoField }}
              />
            </Box>
          )}
        </>
      )}

      <Divider sx={{ my: 3 }} />

      <SectionCard>
        <Stack spacing={0.5}>
          <Typography fontWeight={600}>Информация о промптах:</Typography>
          <Typography variant="body2" color="text.secondary">
            • <Typography component="span" sx={{ fontFamily: 'monospace' }}>psystem</Typography> — системный промпт для всех брендов
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • <Typography component="span" sx={{ fontFamily: 'monospace' }}>pbrand</Typography> — промпт для конкретного бренда
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Переменные:{' '}
            <Typography component="span" sx={{ fontFamily: 'monospace' }}>
              {'{brand}'}, {'{category}'}, {'{tree}'}, {'{town}'}, {'{company}'}
            </Typography>
          </Typography>
        </Stack>
      </SectionCard>
    </Box>
  );
};

export default PromptsSettings;
